import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import {
	type HTMLAttributes,
	type ReactNode,
	type TdHTMLAttributes,
	forwardRef,
} from "react";
import { cn } from "../lib/cn";

export type SortDirection = "asc" | "desc" | null;

// Table root

export interface TableProps extends HTMLAttributes<HTMLTableElement> {}

const Table = forwardRef<HTMLTableElement, TableProps>(
	({ className, ...props }, ref) => (
		<div className="w-full overflow-x-auto">
			<table
				ref={ref}
				className={cn("w-full caption-bottom text-sm", className)}
				{...props}
			/>
		</div>
	),
);
Table.displayName = "Table";

// TableHeader (thead)

export interface TableHeaderProps
	extends HTMLAttributes<HTMLTableSectionElement> {}

const TableHeader = forwardRef<HTMLTableSectionElement, TableHeaderProps>(
	({ className, ...props }, ref) => (
		<thead ref={ref} className={cn("[&_tr]:border-b", className)} {...props} />
	),
);
TableHeader.displayName = "TableHeader";

// TableBody (tbody)

export interface TableBodyProps
	extends HTMLAttributes<HTMLTableSectionElement> {}

const TableBody = forwardRef<HTMLTableSectionElement, TableBodyProps>(
	({ className, ...props }, ref) => (
		<tbody
			ref={ref}
			className={cn("[&_tr:last-child]:border-0", className)}
			{...props}
		/>
	),
);
TableBody.displayName = "TableBody";

// TableRow (tr)

export interface TableRowProps extends HTMLAttributes<HTMLTableRowElement> {}

const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(
	({ className, ...props }, ref) => (
		<tr
			ref={ref}
			className={cn(
				"border-b border-border transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted",
				className,
			)}
			{...props}
		/>
	),
);
TableRow.displayName = "TableRow";

// TableCell (td or th with sort support)

export interface TableCellProps extends TdHTMLAttributes<HTMLTableCellElement> {
	as?: "td" | "th";
	sortDirection?: SortDirection;
	onSort?: () => void;
}

const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(
	(
		{ className, as: Tag = "td", sortDirection, onSort, children, ...props },
		ref,
	) => {
		const isSortable = Boolean(onSort);
		const content = isSortable ? (
			<button
				type="button"
				onClick={onSort}
				className="inline-flex items-center gap-1 font-medium hover:text-foreground"
				aria-sort={
					sortDirection === "asc"
						? "ascending"
						: sortDirection === "desc"
							? "descending"
							: undefined
				}
			>
				{children}
				{sortDirection === "asc" ? (
					<ArrowUp className="h-3 w-3" aria-hidden="true" />
				) : sortDirection === "desc" ? (
					<ArrowDown className="h-3 w-3" aria-hidden="true" />
				) : (
					<ArrowUpDown className="h-3 w-3 opacity-40" aria-hidden="true" />
				)}
			</button>
		) : (
			children
		);

		return (
			<Tag
				ref={ref as React.Ref<HTMLTableCellElement>}
				className={cn(
					"p-4 align-middle",
					Tag === "th" && "text-left text-xs font-medium text-mutedForeground",
					className,
				)}
				{...props}
			>
				{content}
			</Tag>
		);
	},
);
TableCell.displayName = "TableCell";

// TableFooter

export interface TableFooterProps
	extends HTMLAttributes<HTMLTableSectionElement> {}

const TableFooter = forwardRef<HTMLTableSectionElement, TableFooterProps>(
	({ className, ...props }, ref) => (
		<tfoot
			ref={ref}
			className={cn(
				"border-t border-border bg-muted/50 font-medium",
				className,
			)}
			{...props}
		/>
	),
);
TableFooter.displayName = "TableFooter";

export { Table, TableHeader, TableBody, TableRow, TableCell, TableFooter };
export type { ReactNode };
